import { t } from "@lingui/macro"
import { observer } from "mobx-react"
import { useCallback, useEffect } from "react"

import { CasesMessagesStore } from "./store"
import { boxWrapperStyles } from "./styled"

import { chatbot_MessageInfoSource } from "src/api"
import { AvyChat } from "src/components/Chat"
import { EAvyChatVariant } from "src/components/Chat/types/chat"
import { CenteredSpinner } from "src/components/CenteredSpinner"
import { environment } from "src/config"
import { getKnowledgeItemSegmentUrl } from "src/helpers/knowledgeItemLinking"
import { reportError } from "src/lib/report"
import { useStore } from "src/store/lib/useStore"
import { StoreProvider } from "src/store/lib/StoreProvider"

interface IProps {
    conversationId: string
    id: number
}

const View = observer((props: IProps) => {
    const { id, conversationId } = props
    const store = useStore(CasesMessagesStore)

    const openLink = useCallback((url: string) => {
        window.open(url, "_blank", "")
    }, [])

    useEffect(() => {
        ;(async () => {
            await store.init(conversationId)
            store.updateChannel(id, conversationId)
        })()
    }, [store, conversationId, id])

    const handleLinkClick = useCallback(
        (item?: chatbot_MessageInfoSource) => {
            if (item === undefined || item.object_type === undefined) {
                openLink("")
                return
            }

            const baseUrl = environment.BASE_URL
            const urlMap: Record<string, string> = {
                information_item: `${baseUrl}/library/information-items/${item.object_id}`,
                notice_board_message: `${baseUrl}/notice-board/${item.object_id}`,
            }

            if (
                item.object_type === "knowledge_item" &&
                item.object_id !== undefined &&
                item.object_id !== null &&
                item.object_id !== ""
            ) {
                const objectId = item.object_id
                ;(async () => {
                    try {
                        const segmentUrl = await getKnowledgeItemSegmentUrl(
                            objectId,
                        )
                        if (segmentUrl !== null) {
                            openLink(`${baseUrl}${segmentUrl}`)
                        } else if (
                            store.propertyObjectSegmentId !== undefined &&
                            store.propertyObjectSegmentId !== null &&
                            store.propertyObjectSegmentId > 0
                        ) {
                            openLink(
                                `${baseUrl}/chatbot/properties/${store.propertyObjectSegmentId}/${objectId}`,
                            )
                        } else {
                            reportError(
                                t`This knowledge item has no published segments and cannot be opened.`,
                            )
                        }
                    } catch (error) {
                        reportError(t`Failed to open knowledge item.`, error)
                    }
                })()
                return
            }

            if (urlMap[item.object_type] !== undefined) {
                openLink(urlMap[item.object_type])
            } else {
                openLink("")
            }
        },
        [openLink, store.propertyObjectSegmentId],
    )

    if (!store.initialized) {
        return <CenteredSpinner height="100vh" />
    }

    return (
        <AvyChat
            variant={EAvyChatVariant.Chat}
            messages={store.messages}
            boxWrapperStyles={boxWrapperStyles}
            handleSubmit={(images: IPersistedFile[], text: string) =>
                store.submitCommentForm(props.conversationId, images, text)
            }
            handleSourceClick={handleLinkClick}
        />
    )
})

export const CasesMessages = (props: IProps) => (
    <StoreProvider Store={CasesMessagesStore}>
        <View {...props} />
    </StoreProvider>
)
